"use client"

import { useState } from "react"
import { Elements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import Layout from "../Layout/Layout"
import "./pricing.css"
import StripeCheckoutButton from "../components/stripeCheckoutButton"

// Use the publishable key from your Stripe Dashboard
const stripePromise = loadStripe(
  "pk_test_51RRHLoPT4OZhHvkf3F5ZURtqrGNjLiqE2Vr69EkQurdML5ACb4AphtXO9o89rFDtx7Eg5MIl9wXrIuaTU27AvPLS00I3tTnG3h"
)

const plans = [
  {
    id: 1,
    title: "Starter",
    credits: 1,
    price: 5,
    description: "Perfect for trying out our service",
    icon: "🚀",
    features: ["1 Contract Analysis", "Basic Risk Assessment", "PDF Report", "Email Support"],
  },
  {
    id: 2,
    title: "Professional",
    credits: 3,
    price: 12,
    description: "Great for small businesses",
    icon: "💼",
    features: [
      "3 Contract Analyses",
      "Advanced Risk Assessment",
      "Detailed PDF Reports",
      "Priority Email Support",
      "Risk Recommendations",
    ],
  },
  {
    id: 3,
    title: "Enterprise",
    credits: 5,
    price: 20,
    description: "Best value for frequent users",
    icon: "🏢",
    features: [
      "5 Contract Analyses",
      "Comprehensive Risk Assessment",
      "Executive Summary Reports",
      "Phone & Email Support",
      "Custom Risk Recommendations",
      "Bulk Analysis Tools",
    ],
  },
]

const planDetails = {
  1: { amount: 500, credits: 1 },
  2: { amount: 1200, credits: 3 },
  3: { amount: 2000, credits: 5 },
}

const Pricing = () => {
  const [selectedPlanId, setSelectedPlanId] = useState(null)
  const [showModal, setShowModal] = useState(false)

  const openPaymentModal = (planId) => {
    setSelectedPlanId(planId)
    setShowModal(true)
  }

  const closePaymentModal = () => {
    setSelectedPlanId(null)
    setShowModal(false)
  }

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId)

  const PricingCard = ({ plan }) => (
    <div className={`pricing-card ${plan.id === selectedPlanId ? "selected" : ""}`}>
      <div className="card-content">
        <div className="card-header">
          <div className="plan-icon">{plan.icon}</div>
          <h3 className="plan-title">{plan.title}</h3>
          <div className="plan-price">${plan.price}</div>
          <div className="plan-period">per credit pack</div>
          <p className="plan-description">{plan.description}</p>
        </div>

        <ul className="plan-features">
          {plan.features.map((feature, index) => (
            <li key={index} className="feature-item-card">
              <div className="feature-check">
                <span style={{ color: "white", fontSize: "0.8rem" }}>✓</span>
              </div>
              <span className="feature-text-card">{feature}</span>
            </li>
          ))}
        </ul>

        <button className="buy-button" onClick={() => openPaymentModal(plan.id)}>
          <span>💳</span>
          Buy {plan.credits} Credit{plan.credits > 1 ? "s" : ""}
        </button>
      </div>
    </div>
  )

  const PaymentModal = () => {
    if (!showModal || !selectedPlan) return null

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={closePaymentModal}
      >
        <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">Purchase {selectedPlan.title} Plan</h3>
            <p className="modal-subtitle">
              {selectedPlan.credits} Credit{selectedPlan.credits > 1 ? "s" : ""} for ${selectedPlan.price}
            </p>
          </div>

          <Elements
            stripe={stripePromise}
            options={{
              mode: "payment",
              currency: "usd",
              amount: planDetails[selectedPlanId]?.amount || 500,
              wallets: { applePay: "never", googlePay: "never" },
            }}
          >
            <StripeCheckoutButton planId={selectedPlanId} onSuccess={closePaymentModal} />
          </Elements>

          <div className="modal-actions">
            <button className="modal-button secondary" onClick={closePaymentModal}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Layout>
      <div className="pricing-container">
        {/* Professional Header */}
        <div className="pricing-header">
          <div className="pricing-header-content">
            <h1 className="pricing-title">Choose Your Plan</h1>
            <p className="pricing-subtitle">
              Select the perfect credit package for your contract analysis needs. All plans include our advanced AI-powered
              risk assessment and detailed reporting.
            </p>
            <div className="pricing-features">
              <div className="feature-item">
                <span className="feature-icon">⚡</span>
                <span className="feature-text">Instant Analysis</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🔒</span>
                <span className="feature-text">Secure & Private</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">📊</span>
                <span className="feature-text">Detailed Reports</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🎯</span>
                <span className="feature-text">Risk Assessment</span>
              </div>
            </div>
          </div>
        </div>

        {/* Value Proposition */}
        <div className="value-section">
          <h2 className="value-title">Why Choose Our Contract Analysis?</h2>
          <p className="value-description">
            Our AI-powered platform provides comprehensive contract analysis, identifying potential risks and providing
            actionable recommendations to protect your business interests.
          </p>
          <div className="value-grid">
            <div className="value-item">
              <div className="value-item-icon">🤖</div>
              <h4 className="value-item-title">AI-Powered Analysis</h4>
              <p className="value-item-text">
                Advanced machine learning algorithms analyze your contracts for hidden risks and opportunities.
              </p>
            </div>
            <div className="value-item">
              <div className="value-item-icon">⚡</div>
              <h4 className="value-item-title">Lightning Fast</h4>
              <p className="value-item-text">Get comprehensive analysis results in minutes, not hours or days.</p>
            </div>
            <div className="value-item">
              <div className="value-item-icon">🎯</div>
              <h4 className="value-item-title">Actionable Insights</h4>
              <p className="value-item-text">
                Receive specific recommendations and action items to improve your contract terms.
              </p>
            </div>
            <div className="value-item">
              <div className="value-item-icon">🔒</div>
              <h4 className="value-item-title">Bank-Level Security</h4>
              <p className="value-item-text">Your sensitive documents are protected with enterprise-grade encryption.</p>
            </div>
          </div>
        </div>

        {/* Pricing Plans */}
        <div className="pricing-plans">
          {plans.map((plan) => (
            <PricingCard key={plan.id} plan={plan} />
          ))}
        </div>

        {/* Footer */}
        <div className="pricing-footer">
          <h3 className="footer-title">Need a Custom Solution?</h3>
          <p className="footer-text">
            Looking for enterprise-level features or bulk pricing? We'd love to discuss a custom plan that fits your
            specific needs.
          </p>
          <a href="/contact" className="contact-link">
            Contact Our Sales Team →
          </a>
        </div>

        {/* Payment Modal */}
        <PaymentModal />
      </div>
    </Layout>
  )
}

export default Pricing